"use client";

import { Suspense } from "react";

import { BugReportButton } from "@/features/bug-report/bug-report-button";
import { TesterPanel } from "@/features/tester";
import { useUserProfile } from "@/hooks/useAuth";
import { ErrorBoundary } from "@/shared/lib/error-boundary";
import { Footer } from "@/widgets/footer/footer";
import { Header } from "@/widgets/header/header";
import { HeaderLayoutWrapper } from "@/widgets/header/header-layout-wrapper";
import { ErrorLogWidget } from "@/widgets/dev/error-log-widget";

/**
 * Layout for publicly browsable pages (tournaments, organizations, leaderboard, scrims).
 * Shows the same Header/shell as (protected), but does NOT require authentication.
 * Auth-dependent UI (buttons, actions) is handled at the page/component level.
 * Does NOT block rendering on auth — content is shown immediately.
 */
export default function BrowseLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  // Trigger user profile fetch if authenticated (for conditional UI in child pages).
  // useUserProfile has `enabled: isAuthenticated` — it won't fire for anonymous users.
  useUserProfile();

  // min-h-svh у main (KAN-1397): в HTML страниц с клиентским bailout лежит короткая
  // серверная сводка, и высокий футер (400–520 px) попадал в первый экран; приход
  // клиентского контента выталкивал его — CLS 0,21 десктоп / 0,48 мобайл на организации.
  return (
    <ErrorBoundary>
      <HeaderLayoutWrapper header={<Header />}>
        <main id="main-content" className="flex-1 relative z-10 min-h-svh">
          <Suspense fallback={null}>{children}</Suspense>
          <BugReportButton />
          <TesterPanel />
        </main>
        <Footer />
        <ErrorLogWidget />
      </HeaderLayoutWrapper>
    </ErrorBoundary>
  );
}
